import { ref, watch, watchEffect } from 'vue'

import { updateNotificationSubscriptionReadStatus } from '@/api/notification-subscriptions.api'
import { serializeMoney } from '@/lib/adapters/common.adapter'
import { handleErrorMessage } from '@/lib/helpers'
import { request } from '@/lib/services/request.services'
import { type MoneyType, RequestStatus } from '@/lib/types/common.types'
import { useErrorStore } from '@/stores/error.stores'
import { useUserStore } from '@/stores/user.stores'
import type {
  CampaignParticipantDetailsType,
  CampaignParticipantType,
  ParticipantStatus,
} from '@/types/campaignParticipant.types'

export const useUpdateCampaignParticipant = (campaignId: number) => {
  const errorStore = useErrorStore()
  const error = ref<Error | null>(null)
  const isLoading = ref(false)

  const updateTodoList = async (
    participantId: number | string,
    todo: CampaignParticipantType['todo'],
  ) => {
    try {
      isLoading.value = true
      error.value = null
      await request.patch(`/v2/offers/${campaignId}/participants/${participantId}/todo`, { todo })
    } catch (e: unknown) {
      error.value = e as Error
      errorStore.setError(handleErrorMessage(e))
    } finally {
      isLoading.value = false
    }
  }

  const addFixedFee = async (participantId: number | string, fixedFee: MoneyType) => {
    try {
      isLoading.value = true
      error.value = null
      await request.put(`/v2/offers/${campaignId}/participants/${participantId}/payment-amount`, {
        payment_amount: serializeMoney({
          amount: fixedFee.amount,
          currency: fixedFee.currency,
        }),
      })
    } catch (e: unknown) {
      error.value = e as Error
      errorStore.setError(handleErrorMessage(e))
    } finally {
      isLoading.value = false
    }
  }

  const removeFixedFee = async (participantId: number | string) => {
    try {
      isLoading.value = true
      error.value = null
      await request.put(`/v2/offers/${campaignId}/participants/${participantId}/payment-amount`, {
        payment_amount: null,
      })
    } catch (e: unknown) {
      error.value = e as Error
      errorStore.setError(handleErrorMessage(e))
    } finally {
      isLoading.value = false
    }
  }

  const addComment = async (participantId: number | string, comment: string) => {
    try {
      isLoading.value = true
      error.value = null
      await request.post(`/v2/offer-users/${participantId}/comments`, { comment })
    } catch (e: unknown) {
      error.value = e as Error
      errorStore.setError(handleErrorMessage(e))
    } finally {
      isLoading.value = false
    }
  }

  const editComment = async (
    participantId: number | string,
    commentId: number,
    comment: string,
  ) => {
    try {
      isLoading.value = true
      error.value = null
      await request.patch(`/v2/offer-users/${participantId}/comments/${commentId}`, { comment })
    } catch (e: unknown) {
      error.value = e as Error
      errorStore.setError(handleErrorMessage(e))
    } finally {
      isLoading.value = false
    }
  }

  const deleteComment = async (participantId: number | string, commentId: number) => {
    try {
      isLoading.value = true
      error.value = null
      await request.delete(`/v2/offer-users/${participantId}/comments/${commentId}`)
    } catch (e: unknown) {
      error.value = e as Error
      errorStore.setError(handleErrorMessage(e))
    } finally {
      isLoading.value = false
    }
  }

  const updateParticipantStatus = async (
    participantId: number | string,
    status: ParticipantStatus,
  ) => {
    try {
      isLoading.value = true
      error.value = null
      const endpoint = `/v2/offers/${campaignId}/participants/${participantId}/update-status`
      await request.patch(endpoint, { status })
    } catch (e: unknown) {
      error.value = e as Error
      errorStore.setError(handleErrorMessage(e))
    } finally {
      isLoading.value = false
    }
  }

  return {
    updateTodoList,
    addFixedFee,
    addComment,
    editComment,
    deleteComment,
    updateParticipantStatus,
    removeFixedFee,
    error,
    isLoading,
  }
}

export const useGetParticipantDetails = (
  campaignId: number | string,
  onNotificationClear: () => void,
  participantId?: number | string,
) => {
  const errorStore = useErrorStore()
  const userStore = useUserStore()
  const error = ref<Error | null>(null)
  const isLoading = ref(false)
  const status = ref<RequestStatus>(RequestStatus.Idle)

  const data = ref<CampaignParticipantDetailsType | null>(null)

  const getParticipantDetails = async (participantId: number | string) => {
    try {
      isLoading.value = true
      status.value = RequestStatus.Loading
      error.value = null

      const participantResponse = await request.get(
        `/v2/offers/${campaignId}/participants/${participantId}`,
      )
      const participantActivitiesResponse = await request.get(
        `/v2/offer-users/${participantId}/activities?include[]=has_unread_notification`,
      )

      data.value = {
        ...participantResponse.data,
        activity: participantActivitiesResponse.data.data,
      }
    } catch (e: unknown) {
      status.value = RequestStatus.Error
      error.value = e as Error
      errorStore.setError(handleErrorMessage(e))
    } finally {
      status.value = RequestStatus.Success
      isLoading.value = false
    }
  }

  const getActivities = async (participantId: number | string) => {
    try {
      isLoading.value = true
      error.value = null
      const response = await request.get(
        `/v2/offer-users/${participantId}/activities?include[]=has_unread_notification`,
      )
      data.value = { ...data.value, activity: response.data.data }
    } catch (e: unknown) {
      error.value = e as Error
      errorStore.setError(handleErrorMessage(e))
    } finally {
      isLoading.value = false
    }
  }

  watchEffect(() => {
    if (participantId) {
      void getParticipantDetails(participantId)
    }
  })

  watch(
    [() => status.value, () => userStore.subscriberId],
    async ([statusValue, subscriberId]: [RequestStatus, string]) => {
      if (statusValue === RequestStatus.Success && subscriberId && participantId) {
        await updateNotificationSubscriptionReadStatus(
          subscriberId,
          encodeURIComponent(`campaign:${campaignId}/participant:${participantId}`),
          'read',
        )
        onNotificationClear()
      }
    },
    { immediate: true },
  )

  return { data, getParticipantDetails, getActivities, error, isLoading, status }
}
